// Classe padrão dos campos de formulário (select/input) do Minha Conta V2 —
// compartilhada entre ScreenSecurity.vue, ScreenSelfExclusion.vue e
// ScreenTemporaryPause.vue. `flex items-center` porque o select nativo não
// centraliza o texto sozinho com `h-[40px]` fixo (sobra embaixo, cola no topo).
export const USER_V2_FIELD_CLASS =
  'flex h-[40px] w-full items-center rounded-[12px] border-2 border-v2-default-500 bg-v2-default-300 px-[12px] text-[14px] text-v2-foreground outline-none';

// Saldo de moeda alternativa (Coins/Diamonds/Gems) do BalancesBar — ainda sem
// endpoint no core.
export interface UserV2CoinBalance {
  key: 'coins' | 'diamonds' | 'gems';
  title: string;
  prefix: string;
  value: number;
  icon: string;
}

export type UserV2IconName =
  | 'user'
  | 'bell'
  | 'document'
  | 'key'
  | 'statement'
  | 'login'
  | 'bets'
  | 'transactions'
  | 'responsible'
  | 'income'
  | 'logout'
  | 'chevron-right'
  | 'chevron-down'
  | 'chevron-left'
  | 'copy'
  | 'edit'
  | 'gift'
  | 'pause'
  | 'user-block'
  | 'wallet'
  | 'star'
  | 'account-limits'
  | 'temporary-pause-clock'
  | 'self-exclusion';

export interface UserV2MenuEntry {
  label: string;
  icon: UserV2IconName;
  to?: string;
  children?: UserV2MenuEntry[];
  // flag de visibilidade (paridade com o menu antigo). Resolvido em runtime
  // por useUserV2Menu — sem flag = sempre visível.
  flag?: 'irpf' | 'notifications' | 'bank-accounts';
  // "Em breve": item aparece travado (blur + cadeado) e sem navegação.
  // Injetado em runtime por useUserV2Menu (ex.: Indique com coming_soon).
  comingSoon?: boolean;
}

// Mapa rota → tela do modal. O middleware userV2Modal intercepta navegações
// pra essas rotas e abre a tela dentro do modal (sem trocar de página).
export interface UserV2ScreenTarget {
  screen: string;
  props?: Record<string, unknown>;
  // desvia pra outra tela quando ?section= bate com uma destas chaves, em
  // vez de abrir `screen` (ver useUserV2Modal.openByPath). Ex.: em
  // /user/account-protection, ?section=exclusion continua abrindo o
  // accordion (ScreenAccountData "Excluir conta" já depende disso),
  // enquanto ?section=self-exclusion (menu "Auto-Exclusão") abre o card
  // dedicado — mesma rota, dois pontos de entrada, telas diferentes.
  sectionScreens?: Record<string, string>;
}

export const USER_V2_PATH_SCREENS: Record<string, UserV2ScreenTarget> = {
  '/user/profile': { screen: 'profile' },
  '/user/data': { screen: 'data' },
  '/user/account': { screen: 'account' },
  '/user/security': { screen: 'security' },
  '/user/login-history': { screen: 'login-history' },
  '/user/history/full': { screen: 'bets-history' },
  '/user/history/casino': {
    screen: 'bets-history',
    props: { initialTab: 'casino' },
  },
  '/user/history/sports': {
    screen: 'bets-history',
    props: { initialTab: 'sports' },
  },
  '/user/history/poker': { screen: 'poker-history' },
  '/user/wallet': { screen: 'wallet' },
  '/user/wallet/transactions': { screen: 'transactions-history' },
  '/user/wallet/last-withdraw': { screen: 'last-withdraw' },
  '/user/notifications': { screen: 'notifications' },
  '/user/achievements': { screen: 'achievements' },
  '/user/refers': { screen: 'refers' },
  '/user/rewards': { screen: 'rewards' },
  '/user/irpf': { screen: 'income-report' },
  '/user/irpf-rendimentos': { screen: 'income-report' },
  // NÃO mapeia /user/self/self-exclusion aqui (página legada com formulário
  // mock, mantida como está) — "Auto-Exclusão" usa
  // /user/account-protection?section=self-exclusion (linha abaixo).
  '/user/temporary-pause': { screen: 'temporary-pause' },
  '/user/account-protection': {
    screen: 'account-protection',
    sectionScreens: { 'self-exclusion': 'self-exclusion' },
  },
  '/user/bank-accounts': { screen: 'bank-accounts' },
};

// Recompensas entra em posições diferentes no mobile (topo da tab "Dados") e
// no desktop (logo abaixo de "Seu Perfil", antes de Notificações) — mesmo
// objeto, reposicionado manualmente na sidebar abaixo em vez de duplicado.
const REWARDS_ENTRY: UserV2MenuEntry = {
  label: 'Prêmios e recompensas',
  icon: 'star',
  to: '/user/rewards',
};

// Menu da tab "Dados" (mobile): só as telas de dados. Perfil e Notificações
// NÃO entram aqui no mobile — são tabs (UserV2Tabs). Base = frame do Figma +
// entradas do menu antigo que não estavam no frame (Carteira, Jogo Responsável
// completo, Recompensas) — paridade 1:1. "Indique e Ganhe" removido a
// pedido do time: ficava travado ("Em breve") sem uso nenhum.
export const USER_V2_MENU: UserV2MenuEntry[] = [
  REWARDS_ENTRY,
  { label: 'Dados da conta', icon: 'document', to: '/user/account' },
  { label: 'Login e segurança', icon: 'key', to: '/user/security' },
  {
    label: 'Carteira',
    icon: 'wallet',
    children: [
      { label: 'Carteira', icon: 'wallet', to: '/user/wallet' },
      {
        label: 'Status de Saque',
        icon: 'transactions',
        to: '/user/wallet/last-withdraw',
      },
      {
        label: 'Contas Bancárias',
        icon: 'wallet',
        to: '/user/bank-accounts',
        flag: 'bank-accounts',
      },
    ],
  },
  { label: 'Histórico de login', icon: 'login', to: '/user/login-history' },
  { label: 'Histórico de apostas', icon: 'bets', to: '/user/history/full' },
  {
    label: 'Histórico de transações',
    icon: 'statement',
    to: '/user/wallet/transactions',
  },
  {
    // Sem `to`: mesmo comportamento do Carteira — clique em qualquer parte da
    // linha expande/recolhe os atalhos, não navega direto. A tela geral
    // (ScreenAccountProtection reúne todos os tipos: limites de depósito/
    // aposta/perda/tempo + timeout, auto-exclusão e exclusão centralizada)
    // continua acessível pelo primeiro atalho, "Limites de proteção".
    label: 'Jogo Responsável',
    icon: 'responsible',
    children: [
      {
        // ?section=deposit: chega com o primeiro accordion (Depósito) já
        // expandido — mesmo mecanismo do botão "Excluir conta"
        // (ScreenAccountData.vue), só que só por query (não dá pra chamar
        // forceOpenAccordion antes de navegar, aqui é um link puro).
        label: 'Limites de proteção',
        icon: 'account-limits',
        to: '/user/account-protection?section=deposit',
      },
      {
        label: 'Auto-Exclusão',
        icon: 'self-exclusion',
        to: '/user/account-protection?section=self-exclusion',
      },
      {
        label: 'Pausa Temporária',
        icon: 'temporary-pause-clock',
        to: '/user/temporary-pause',
      },
    ],
  },
  {
    // label completo (com ano) é montado em runtime por useUserV2Menu
    // (decorate), via income_report_external.menu_title — mesma chave do
    // menu antigo; este label é só o fallback antes do decorate rodar
    label: 'Informe de Rendimentos',
    icon: 'income',
    to: '/user/irpf',
    flag: 'irpf',
  },
];

// Sidebar "Edite seus dados" (desktop): como não há tabs no desktop, Perfil e
// Notificações entram como itens no topo, seguidos das telas de dados.
// Recompensas fica logo abaixo de "Seu Perfil" — puxada pra frente e removida
// do resto do spread (senão apareceria duas vezes).
export const USER_V2_SIDEBAR_MENU: UserV2MenuEntry[] = [
  { label: 'Seu Perfil', icon: 'user', to: '/user/profile' },
  REWARDS_ENTRY,
  {
    label: 'Notificações',
    icon: 'bell',
    to: '/user/notifications',
    flag: 'notifications',
  },
  ...USER_V2_MENU.filter((entry) => entry !== REWARDS_ENTRY),
];
